/** True persisted PK — empty, `new`, and 0 are not ids. Accepts numeric and string IDs. */
export function persistedRecordId(item: { id?: unknown; insertId?: unknown } | null | undefined): number | string | null {
  const raw = item?.id ?? item?.insertId;
  if (raw == null || raw === '') {
    return null;
  }
  if (typeof raw === 'string') {
    return raw === 'new' ? null : raw;
  }
  const n = Number(raw);
  return n > 0 ? n : null;
}

/** Form JSON schema often omits `id`; edit URL is `/modules/:moduleId/:id` or `/modules/settings/:moduleId/:id`. */
export function recordIdFromPath(pathname: string): string | null {
  const path = (pathname || '').split('?')[0];
  const segments = path.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  if (!last || last === 'new') {
    return null;
  }
  return last;
}

export interface FileLink {
  name: string;
  id: string;
}

export function isFileWidget(schema: { widget?: { id?: string } } | undefined): boolean {
  return schema?.widget?.id === 'file';
}

function asId(raw: unknown): string {
  const id = `${raw || ''}`.trim();
  return /^[a-zA-Z0-9_-]+$/.test(id) ? id : '';
}

function asName(raw: unknown, id: string): string {
  const name = `${raw || ''}`.trim();
  if (!name || name === id) {
    return '';
  }
  return name;
}

function asLink(value: unknown): FileLink | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const obj = value as { name?: unknown; id?: unknown };
  const id = asId(obj.id);
  if (!id) {
    return null;
  }
  return { name: asName(obj.name, id), id };
}

function parseJson(raw: unknown): unknown {
  if (typeof raw !== 'string') {
    return raw;
  }
  const text = raw.trim();
  if (!text) {
    return '';
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Parent-column JSON: `[{name, id}, ...]`. `id` is the Drive file id. */
export function serializeFileField(
  files: Array<{ name?: string; id?: string }>,
  multiple: boolean,
): string {
  const source = multiple ? files : files.slice(-1);
  const seen = new Set<string>();
  const items: FileLink[] = [];
  for (const f of source) {
    const id = asId(f.id);
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    items.push({ name: asName(f.name, id), id });
  }
  if (!items.length) {
    return '';
  }
  return JSON.stringify(items);
}

export function parseFileField(raw: unknown): FileLink[] {
  if (raw == null || raw === '' || raw === ' ') {
    return [];
  }
  const parsed = parseJson(raw);
  if (Array.isArray(parsed)) {
    return parsed.map(asLink).filter((item): item is FileLink => !!item);
  }
  const one = asLink(parsed);
  return one ? [one] : [];
}

export function fileLabel(file: { name?: string; id?: string }): string {
  return asName(file?.name, asId(file?.id)) || 'file';
}

export function cleanFileName(name: string, id: string): string {
  return asName(name, id);
}
